import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance, {
  checkTokenValidity,
  CheckUserExpiry,
  getUserDetails,
  isCompanyManagerUser,
  isSuperUserSession,
  handleLegacySessionExpired
} from "../../components/content/axiosConfig";
import PondDetailWorkflowPageHeader from "../../components/custom/PondDetailWorkflowPageHeader";
import PondDetailWorkflowTabs from "../../components/custom/PondDetailWorkflowTabs";
import Swal from 'sweetalert2';
import Preloader from "../../components/custom/preLoader";
import CustomModal from "../../components/custom/customModal";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import {
  formatDisplayDate as formatDateForMobile,
  formatDisplayDateTime as formatDateTime,
} from "../../utils/formatDisplayDate";

const PondNoteViaPondDetail = () => {
    const [notes, setNotes] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [entriesPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const fisherID = getUserDetails().FisherID;
    const staffID = getUserDetails().id;
    const { id } = useParams();
    const [pondDetailDisplayName, setPondDetailDisplayName] = useState('');

    const [modalData, setModalData] = useState({
        Note: "",
        PondDID: id,
        FisherID: fisherID,
        StaffID: staffID
    });

    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);

    const CheckExpiry = useCallback(async () => {
        await CheckUserExpiry();
    }, []);

    const FetchPondNote = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await axiosInstance.get(`/api/PondNote/GetAllNotesViaPondDetailID/${id}`, {
                params: { search: searchTerm }
            });
            const pondNotes = response.data;
            setNotes(pondNotes);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            if (error.response && error.response.status === 401) {
                const auth = handleLegacySessionExpired(error);
                showErrorToast(auth.message);
            } else {
                const errorMessage = error.response?.data?.message || "Failed to fetch pond notes";
                showErrorToast(errorMessage);
            }
            console.error("Error fetching pond notes:", error);
        }
    }, [id, searchTerm]);

    // Handle window resize for responsive design
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        checkTokenValidity();
        CheckExpiry();
        FetchPondNote();
    }, [FetchPondNote, searchTerm, CheckExpiry]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axiosInstance.get(`/api/PondDetail/GetPondDetail`, {
                    params: { id, FisherID: fisherID },
                });
                const name = res.data?.Name;
                if (!cancelled && name != null && String(name).trim()) {
                    setPondDetailDisplayName(String(name).trim());
                }
            } catch {
                /* optional */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [id, fisherID]);

    // Debounce search implementation
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (searchTerm !== undefined) {
                FetchPondNote();
            }
        }, 800); // 800ms debounce delay

        return () => clearTimeout(debounceTimer);
    }, [searchTerm, FetchPondNote]);

    const indexOfLastEntry = currentPage * entriesPerPage;
    const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
    const currentEntries = notes.slice(indexOfFirstEntry, indexOfLastEntry);
    const totalPages = Math.ceil(notes.length / entriesPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const DeleteUser = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                await axiosInstance.delete(`/api/PondNote/DeleteNote/${id}`, {
                    params: { FisherID: fisherID }
                });
                const updatedStaffs = notes.filter((staff) => staff.PondNoteID !== id);
                setNotes(updatedStaffs);
                setIsLoading(false);
                showSuccessToast("Pond note deleted successfully");
            } catch (error) {
                setIsLoading(false);
                if (error.response && error.response.status === 401) {
                const auth = handleLegacySessionExpired(error);
                showErrorToast(auth.message);
            } else {
                    const errorMessage = error.response?.data?.message || "Failed to delete pond notes";
                    showErrorToast(errorMessage);
                }
                console.error("Error deleting pond notes:", error);
            }
        }
    };

    const openModal = (data = null) => {
        if (data) {
            setIsEditing(true);
            setEditId(data.PondNoteID);
            setModalData({
                Note: data.PondNote,
                PondDID: data.PondDID,
                FisherID: fisherID,
                StaffID: staffID
            });
        } else {
            setIsEditing(false);
            setEditId(null);
            setModalData({
                Note: "",
                PondDID: id,
                FisherID: fisherID,
                StaffID: staffID
            });
        }
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
    };

    const handleChange = (e) => {
        const { id, name, value, type, checked } = e.target;

        if (type === "radio") {
            setModalData({
                ...modalData,
                [name]: value === "true",
            });
        } else {
            setModalData({
                ...modalData,
                [id]: type === "checkbox" ? checked : value,
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            if (!modalData.Note.trim()) {
                showErrorToast("Note cannot be empty");
                setIsLoading(false);
                return;
            }

            if (isEditing) {
                await axiosInstance.put(`/api/PondNote/UpdateNote/${editId}`, modalData);
                showSuccessToast("Pond notes updated successfully");
            } else {
                await axiosInstance.post(`/api/PondNote/AddNote`, modalData);
                showSuccessToast("Pond notes added successfully");
            }
            FetchPondNote();
            setIsLoading(false);
            closeModal();
        } catch (error) {
            setIsLoading(false);
            if (error.response && error.response.status === 401) {
                const auth = handleLegacySessionExpired(error);
                showErrorToast(auth.message);
            } else {
                const errorMessage = error.response?.data?.message || "Failed to save pond note";
                showErrorToast(errorMessage);
            }
            console.error("Error submitting form:", error);
        }
    };

    return (
        isLoading ? <Preloader /> :
            <div className="donezo-page donezo-workflow-page py-3">
                <div className="container-fluid px-3 px-md-4">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4">
                    <div className="mb-3 mb-md-0 flex-grow-1" style={{ minWidth: 0 }}>
                        <PondDetailWorkflowPageHeader
                            pondDetailId={id}
                            section="notes"
                            detailName={pondDetailDisplayName}
                            noBottomMargin
                        />
                    </div>
                    <div className="ms-md-auto">
                        <button
                            type="button"
                            className="btn btn-success btn-rounded px-3 px-md-4 rounded-pill"
                            onClick={() => openModal()}
                        >
                            <i className="fa fa-plus me-1"></i>
                            {windowWidth > 576 ? "Add Notes" : "Add"}
                        </button>
                    </div>
                </div>

                <PondDetailWorkflowTabs id={id} active="notes" />

                {/* Tab Content */}
                <div className="card border-0 shadow-sm donezo-workflow-card">
                    <div className="tab-content p-3 p-md-4">
                        <div className="input-group mb-3">
                            <div className="flex-fill position-relative">
                                <div className="input-group">
                                    <div
                                        className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                                        style={{ zIndex: 10 }}
                                    >
                                        <i className="fa fa-search opacity-5"></i>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control px-35px bg-light"
                                        placeholder="Search Notes..."
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Desktop Table */}
                        <div className="d-none d-md-block">
                            <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Pond Name</th>
                                        <th>Notes</th>
                                        <th>By Staff</th>
                                        <th>Update Date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentEntries.map((web) => (
                                        <tr key={web.PondNoteID}>
                                            <td>{web.PondName}</td>
                                            <td title={web.PondNote} >{web.PondNote.length > 50 ? web.PondNote.slice(0, 50) + '...' : web.PondNote}</td>
                                            <td>{web.StaffName}</td>
                                            <td>{formatDateTime(web.UpdatedAt)}</td>
                                            <td>
                                                <div className="donezo-row-actions">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-primary"
                                                    title="Edit note"
                                                    aria-label="Edit note"
                                                    onClick={() => openModal(web)}
                                                >
                                                    <i className="fa fa-pencil" aria-hidden />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger"
                                                    title="Delete note"
                                                    aria-label="Delete note"
                                                    onClick={() => DeleteUser(web.PondNoteID)}
                                                >
                                                    <i className="fa fa-trash" aria-hidden />
                                                </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Cards */}
                        <div className="d-md-none">
                            {currentEntries.map((web) => (
                                <div key={web.PondNoteID} className="card mb-3 shadow-sm">
                                    <div className="card-body p-3">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="card-title mb-0">{web.PondName}</h5>
                                            <div className="donezo-row-actions">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-primary"
                                                    title="Edit note"
                                                    aria-label="Edit note"
                                                    onClick={() => openModal(web)}
                                                >
                                                    <i className="fa fa-pencil" aria-hidden />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger"
                                                    title="Delete note"
                                                    aria-label="Delete note"
                                                    onClick={() => DeleteUser(web.PondNoteID)}
                                                >
                                                    <i className="fa fa-trash" aria-hidden />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="row mt-2">
                                            <div className="col-12">
                                                <p className="mb-1"><small className="text-muted">Note:</small> {web.PondNote}</p>
                                            </div>
                                            <div className="col-6">
                                                <p className="mb-1"><small className="text-muted">Updated:</small> {formatDateForMobile(web.UpdatedAt)}</p>
                                            </div>
                                            <div className="col-6">
                                                <p className="mb-1"><small className="text-muted">By:</small> {web.StaffName}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        <div className="d-md-flex align-items-center mt-3">
                            <div className="me-md-auto text-md-left text-center mb-2 mb-md-0">
                                Showing {notes.length === 0 ? indexOfFirstEntry : indexOfFirstEntry + 1} to{" "}
                                {indexOfLastEntry > notes.length ? notes.length : indexOfLastEntry} of {notes.length} entries
                            </div>
                            <ul className="pagination mb-0 justify-content-center">
                                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                    >
                                        {windowWidth < 768 ? '«' : 'Previous'}
                                    </button>
                                </li>
                                {windowWidth < 768 ? (
                                    <li className="page-item active">
                                        <span className="page-link">
                                            {currentPage} / {totalPages}
                                        </span>
                                    </li>
                                ) : (
                                    [...Array(totalPages)].map((_, index) => (
                                        <li
                                            key={index}
                                            className={`page-item ${index + 1 === currentPage ? "active" : ""}`}
                                        >
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(index + 1)}
                                            >
                                                {index + 1}
                                            </button>
                                        </li>
                                    ))
                                )}
                                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                    >
                                        {windowWidth < 768 ? '»' : 'Next'}
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>


                <CustomModal
                    isOpen={modalIsOpen}
                    onClose={closeModal}
                    title={isEditing ? "Edit Pond Note" : "Add Pond Note"}
                    showButton={true}
                    modalSize="modal-lg"
                    minHeight="400px"
                    maxHeight="90vh"
                    buttonText="Submit"
                    onSubmit={handleSubmit}
                >
                    <div className="card border-0">
                        <div className="tab-content p-3">
                            <div className="tab-pane fade show active" id="allTab">
                                <label
                                    htmlFor="Note"
                                    className="d-flex align-items-center fs-13px text-gray-600"
                                >
                                    Note &nbsp; <span>*</span>
                                </label>
                                <div className="mb-15px">
                                    <textarea
                                        className="form-control fs-13px"
                                        placeholder="Note"
                                        id="Note"
                                        name="Note"
                                        value={modalData.Note}
                                        onChange={handleChange}
                                        rows={5}
                                        style={{ resize: "vertical", minHeight: "7.5rem" }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </CustomModal>
                </div>
            </div>
            
    );
};

export default PondNoteViaPondDetail;